import { storage } from '../storage';
import {
  emptyEntitlements,
  type BillingService,
  type Entitlements,
  type PurchaseResult,
} from './billingService';

const ENTITLEMENTS_KEY = 'pa.entitlements';

/**
 * Stub billing provider. No real money, no network. When FEATURES.payments is
 * OFF it reports zero entitlements and refuses purchases — this is the v1
 * dormant state. When turned ON it simulates a working store by persisting
 * "purchases" locally, which is enough for the rest of the app (skins shop,
 * ad-free) to be exercised end to end before a real provider is wired in.
 *
 * To ship real payments: implement this same interface against a provider SDK.
 */
export class StubBillingService implements BillingService {
  private readonly enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  async getEntitlements(): Promise<Entitlements> {
    if (!this.enabled) return emptyEntitlements();
    return storage.getJSON<Entitlements>(ENTITLEMENTS_KEY, emptyEntitlements());
  }

  async ownsProduct(productId: string): Promise<boolean> {
    const e = await this.getEntitlements();
    if (productId === 'ad_free') return e.adFree;
    return e.ownedSkins.includes(productId);
  }

  async isSubscribed(planId: string): Promise<boolean> {
    const e = await this.getEntitlements();
    return e.subscriptions.includes(planId);
  }

  async purchase(productId: string): Promise<PurchaseResult> {
    if (!this.enabled) return { ok: false, productId, error: 'billing_disabled' };
    const e = await this.getEntitlements();
    if (productId === 'ad_free') e.adFree = true;
    else if (!e.ownedSkins.includes(productId)) e.ownedSkins.push(productId);
    storage.setJSON(ENTITLEMENTS_KEY, e);
    return { ok: true, productId };
  }

  async subscribe(planId: string): Promise<PurchaseResult> {
    if (!this.enabled) return { ok: false, productId: planId, error: 'billing_disabled' };
    const e = await this.getEntitlements();
    if (!e.subscriptions.includes(planId)) e.subscriptions.push(planId);
    // An ad-free subscription also grants the ad-free entitlement.
    if (planId.startsWith('ad_free')) e.adFree = true;
    storage.setJSON(ENTITLEMENTS_KEY, e);
    return { ok: true, productId: planId };
  }

  async restorePurchases(): Promise<Entitlements> {
    return this.getEntitlements();
  }
}
